import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import type {
  ReferralFilters,
  ReferralFiltersPayload,
} from 'src/types/referral-v2';
import {
  getFormattedDateForApi,
  daysDifference,
  getDateRangeOfDaysDifference,
  validateDate,
} from 'src/components/referral-v2/common';
import useAnalyticsQuery from 'src/components/analytics/useAnalyticsQuery';
import {resolveDateRange} from 'src/utils/date-range';
import {getTimeFrameOptions} from 'src/utils/referral-v2';
import {
  Panel,
  PanelBody,
  PanelHeader,
  PanelFooter,
  Chip,
  Button,
  SubTitleLarge,
  SimpleDropdown,
  Input,
  CircularLoader,
} from '@spaced-out/ui-design-system/lib/components/index';
import css from 'src/components/referral-v2/referral-filter/referral-filter.css';


type ReferralFilterProps = {
  filters: ReferralFilters[];
  handleApplyFilter: (filters: ReferralFiltersPayload[]) => unknown;
  loading: boolean;
  isOpen: boolean;
  onClose: () => unknown;
  selectedTab: string;
};

const ReferralFilter = ({
  filters,
  handleApplyFilter,
  isOpen,
  onClose,
  loading,
  selectedTab,
}: ReferralFilterProps) => {
  const {t, makeKey} = useLabelTranslation();
  const timeFrameOptions = getTimeFrameOptions(t);
  const [query, handleQueryChange] = useAnalyticsQuery();
  const {start_date = '', end_date = ''} = resolveDateRange(query);
  const start_date_formatted_date = getFormattedDateForApi(start_date);
  const end_date_formatted_date = getFormattedDateForApi(end_date);

  const defaultFilters = () => {
    const defaultTimeFrame = {
      key: 'timeframe',
      values: getDateRangeOfDaysDifference('90'),
    } as const;

    const initialFilters = filters.map((filter) => ({
      key: filter.key,
      values: [],
    }));
    // @ts-expect-error - TS2345 - Argument of type '{ readonly key: "timeframe"; readonly values: { end_date: string; start_date: string; }; }' is not assignable to parameter of type '{ key: string; values: never[]; }'.
    initialFilters.push(defaultTimeFrame);
    return initialFilters;
  };

  const defaultTimeFrameValue = () => {
    const day = daysDifference(
      start_date_formatted_date,
      end_date_formatted_date,
    );
    if (timeFrameOptions.some((option) => option.key === day)) {
      return day;
    } else {
      return 'date-range';
    }
  };

  const timeFilterQuery = {
    start_date: start_date_formatted_date,
    end_date: end_date_formatted_date,
  } as const;

  const [selectedFilters, setSelectedFilters] = React.useState<
    ReferralFiltersPayload[]
  >([]);

  const [showDateRangeFilter, setShowDateRangeFilter] = React.useState<boolean>(
    defaultTimeFrameValue() === 'date-range',
  );

  const handleChangeFilter = (key: string, value: string) => {
    let newFilters = [...selectedFilters];
    const selectedFilter = newFilters.some((filter) => filter.key === key);

    if (selectedFilter) {
      newFilters = newFilters.map(({key: field, values}) => {
        if (field === key && Array.isArray(values)) {
          const updatedValues = values.includes(value)
            ? values.filter((element) => element !== value)
            : [...values, value];

          return {
            key: field,
            values: updatedValues,
          };
        }
        return {
          key: field,
          values,
        };
      });
    } else {
      newFilters.push({key, values: [value]});
    }
    setSelectedFilters(newFilters);
  };

  const onChangeTimeFrame = (value: string) => {
    if (value !== 'date-range') {
      setShowDateRangeFilter(false);
      const timeframeQuery = getDateRangeOfDaysDifference(value);
      // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
      handleQueryChange({
        start_date: {$set: timeframeQuery.start_date},
        end_date: {$set: timeframeQuery.end_date},
      });
    } else {
      setShowDateRangeFilter(true);
    }
  };

  const getFilterOptions = (
    options:
      | undefined
      | {
          end_date: string;
          start_date: string;
        }
      | Array<{
          id: string;
          name: string;
        }>,
    // @ts-expect-error - TS7006 - Parameter 'field' implicitly has an 'any' type.
    field,
  ) => {
    if (!Array.isArray(options)) {
      return [];
    }
    return options.map(({id, name}) => {
      return {
        label: field === 'status' ? t(makeKey(name), name) : name,
        key: field === 'program' ? id : name,
      };
    });
  };

  const defaultFilterValues = (filter: ReferralFilters) => {
    const selectedValues = selectedFilters.find(
      (filterData) => filterData.key === filter.key,
    );
    if (!Array.isArray(selectedValues?.values)) {
      return [];
    }
    return selectedValues.values;
  };

  const handleClear = () => {
    setSelectedFilters(defaultFilters());
    const daysDifference = '90';
    onChangeTimeFrame(daysDifference);
    setShowDateRangeFilter(false);
  };

  const handleClose = () => {
    if (defaultTimeFrameValue() !== 'date-range') {
      setShowDateRangeFilter(false);
    }
    onClose();
  };

  const handleApply = () => {
    const updatedFilters = selectedFilters.filter(
      (element) => Array.isArray(element.values) && element.values.length > 0,
    );
    const payloadFilters = [
      ...updatedFilters,
      {
        key: 'timeframe',
        values: timeFilterQuery,
      },
    ];
    handleApplyFilter(payloadFilters);
    handleClose();
  };

  const dropdownInputText = showDateRangeFilter
    ? t('Custom_Date_Range', 'Custom Date Range')
    : timeFrameOptions.find((option) => option.key === defaultTimeFrameValue())
        ?.label;

  React.useEffect(() => {
    setSelectedFilters(defaultFilters());
  }, [selectedTab]);

  return (
    <Panel
      anchor="right"
      initialFocus={2}
      isOpen={isOpen}
      onClose={handleClose}
      size="medium"
      tapOutsideToClose
      classNames={{backdrop: css.panelroot}}
    >
      <PanelHeader onCloseButtonClick={handleClose} size="medium">
        <SubTitleLarge>{t('Filter', 'Filter')}</SubTitleLarge>
      </PanelHeader>
      {loading ? (
        <PanelBody className={css.loader}>
          <CircularLoader />
        </PanelBody>
      ) : (
        <>
          <PanelBody className={css.flexColumn}>
            <SimpleDropdown
              label={t('Timeframe', 'Timeframe')}
              placeholder={`${t('Select', 'Select')}...`}
              options={timeFrameOptions}
              selectedKeys={[
                showDateRangeFilter ? 'date-range' : defaultTimeFrameValue(),
              ]}
              classNames={{wrapper: css.inputContainer}}
              onChange={(option) => onChangeTimeFrame(option.key)}
              // @ts-expect-error - TS2769 - No overload matches this call.
              dropdownInputText={dropdownInputText}
            />
            {showDateRangeFilter && (
              <div>
                <Input
                  label={t('Start_Date', 'Start Date')}
                  onChange={(event) => {
                    event.persist();
                    if (validateDate(event.target.value)) {
                      // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
                      handleQueryChange({
                        start_date: {$set: event.target.value},
                        end_date: {$set: end_date_formatted_date},
                      });
                    }
                  }}
                  max={timeFilterQuery.end_date}
                  type="date"
                  // @ts-expect-error - TS17001 - JSX elements cannot have multiple attributes with the same name.
                  max={timeFilterQuery.end_date}
                  value={timeFilterQuery.start_date}
                  classNames={{box: css.startDateInputContainer}}
                />
                <Input
                  label={t('End_Date', 'End Date')}
                  min={timeFilterQuery.start_date}
                  onChange={(event) => {
                    event.persist();
                    if (validateDate(event.target.value)) {
                      // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
                      handleQueryChange({
                        start_date: {$set: start_date_formatted_date},
                        end_date: {$set: event.target.value},
                      });
                    }
                  }}
                  type="date"
                  value={timeFilterQuery.end_date}
                />
              </div>
            )}
            {filters.map((filter) => {
              const filterLabel =
                filter.key.charAt(0).toUpperCase() + filter.key.slice(1);
              return (
                <div className={css.inputContainer}>
                  <SimpleDropdown
                    label={t(makeKey(filterLabel), filterLabel)}
                    placeholder={`${t('Select', 'Select')}...`}
                    classNames={{wrapper: css.inputContainer}}
                    options={getFilterOptions(filter.values, filter.key)}
                    selectedKeys={defaultFilterValues(filter)}
                    optionsVariant="checkbox"
                    onChange={(option) =>
                      handleChangeFilter(filter.key, option.key)
                    }
                  />
                  <div className={css.filterContainer}>
                    {defaultFilterValues(filter).map((value) => {
                      let name = '';
                      if (Array.isArray(filter.values)) {
                        // @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
                        name = filter.values.find((element) =>
                          filter.key === 'program'
                            ? element.id === value
                            : element.name === value,
                        )?.name;
                      }
                      return (
                        <div className={css.removeFilter} key={value}>
                          <Chip
                            dismissable={true}
                            size="medium"
                            semantic="information"
                            onDismiss={() =>
                              handleChangeFilter(filter.key, value)
                            }
                          >
                            {name ?? ''}
                          </Chip>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </PanelBody>
          <PanelFooter>
            <Button onClick={handleClear} type="tertiary">
              {t('Clear_All', 'Clear All')}
            </Button>
            <Button onClick={handleApply} type="secondary">
              {t('Apply', 'Apply')}
            </Button>
          </PanelFooter>
        </>
      )}
    </Panel>
  );
};

export default ReferralFilter;
